import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Mail, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { apiService } from '../services/api.service';

export function EmailVerifyErrorPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const errorType = searchParams.get('error') || 'expired';
    const emailFromUrl = searchParams.get('email') || '';

    const [email, setEmail] = useState(emailFromUrl);
    const [isResending, setIsResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    const getErrorInfo = (type: string) => {
        switch (type) {
            case 'expired':
                return {
                    title: 'Verification Link Expired',
                    description: 'This verification link has expired. Please request a new one.',
                };
            case 'invalid':
                return {
                    title: 'Invalid Verification Link',
                    description: 'This verification link is invalid or has already been used.',
                };
            case 'consumed':
                return {
                    title: 'Link Already Used',
                    description: 'This verification link has already been used. If you\'re still having trouble logging in, try requesting a new link.',
                };
            default:
                return {
                    title: 'Verification Failed',
                    description: 'We couldn\'t verify your email. Please try again.',
                };
        }
    };

    const handleResendVerification = async () => {
        if (!email.trim()) {
            toast.error('Please enter your email address');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        setIsResending(true);
        try {
            await apiService.resendVerificationEmail(email);
            setResendSuccess(true);
            toast.success('Verification email sent! Check your inbox.');
        } catch (error: any) {
            // Show generic message to prevent email enumeration
            toast.error(error.message || 'Unable to send verification email. Please try again later.');
        } finally {
            setIsResending(false);
        }
    };

    const errorInfo = getErrorInfo(errorType);

    if (resendSuccess) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                            <Mail className="w-8 h-8 text-green-600" />
                        </div>
                        <CardTitle className="text-2xl">Check Your Email</CardTitle>
                        <CardDescription>
                            We've sent a new verification link to <strong>{email}</strong>
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground text-center">
                            Click the link in the email to verify your account. The link will expire in 24 hours.
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setResendSuccess(false)}
                                className="w-full"
                            >
                                Didn't receive it? Try again
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/login')}
                                className="w-full"
                            >
                                Back to Login
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <Card className="max-w-md w-full">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-8 h-8 text-destructive" />
                    </div>
                    <CardTitle className="text-2xl">{errorInfo.title}</CardTitle>
                    <CardDescription>{errorInfo.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isResending}
                        />
                        <p className="text-xs text-muted-foreground">
                            Enter the email you used to sign up
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Button
                            onClick={handleResendVerification}
                            disabled={isResending}
                            className="w-full"
                        >
                            {isResending ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Resend Verification Email
                                </>
                            )}
                        </Button>

                        <Button
                            variant="ghost"
                            onClick={() => navigate('/login')}
                            className="w-full"
                        >
                            Back to Login
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
